/**
 * TaskProgressBanner — persistent in-context progress surface for long
 * content-plan tasks (LT1 fix). Wires the EXISTING `useContentStrategyTask`
 * subscription (backend task doc `phases` + `currentPhaseKey`); it never
 * replaces the task infrastructure (preserve-list #7).
 *
 * Invariants (designer.md):
 *   - lives in the launching surface (wizard strip area), survives dialog
 *     close and tab switches — ownership is page-level state;
 *   - FULL error text is persistently visible (no hover-only truncation);
 *   - failure keeps recovery actions: resume-first when the task result
 *     carries resume info (B5), else Retry (when the launcher provided one),
 *     plus a Details popup for long payloads.
 */

import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, LinearProgress, Paper, Stack, Typography,
} from '@mui/material';
import { SDialog } from '@modules/ui-components';
import { CONTENT_PLAN } from '@test-kit/selectors/content-plan';
import { useContentStrategyTask } from '../../hooks/useContentStrategyTask';

export interface TaskProgressBannerProps {
  projectId: string;
  taskId: string;
  /** Human label of the launched work, e.g. "Create strategy". */
  label: string;
  onDismiss: () => void;
  /** Re-dispatch the SAME work (start over). Optional — hidden when absent. */
  onRetry?: () => void;
  /** Opt-in navigation to the result surface (explicit user click only). */
  onViewResult?: { label: string; onClick: () => void };
}

interface ResumeInfo {
  phase?: string;
}

/** B5 forward-compat: surface resume info if the task result carries it. */
function extractResumeInfo(task: unknown): ResumeInfo | null {
  const result = (task as { result?: Record<string, unknown> } | null)?.result;
  if (!result || typeof result !== 'object') return null;
  const resume = (result as { resume?: unknown }).resume;
  if (resume && typeof resume === 'object') {
    return { phase: (resume as { phase?: string }).phase };
  }
  const resumeFromPhase = (result as { resumeFromPhase?: unknown }).resumeFromPhase;
  if (typeof resumeFromPhase === 'string') return { phase: resumeFromPhase };
  return null;
}

export const TaskProgressBanner: React.FC<TaskProgressBannerProps> = ({
  projectId, taskId, label, onDismiss, onRetry, onViewResult,
}) => {
  const {
    task, currentPhaseName, status, error, progressPercent, loading,
  } = useContentStrategyTask(projectId, taskId);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const resumeInfo = useMemo(() => extractResumeInfo(task), [task]);
  const isError = status === 'error';
  const isDone = status === 'completed';
  const isRunning = !isError && !isDone;

  const detailsPayload = useMemo(() => {
    try {
      return JSON.stringify(
        {
          taskId,
          status,
          currentPhase: currentPhaseName,
          error,
          result: (task as { result?: unknown } | null)?.result,
        },
        null,
        2,
      );
    } catch {
      return String(error ?? '');
    }
  }, [taskId, status, currentPhaseName, error, task]);

  return (
    <Paper
      variant="outlined"
      data-testid={CONTENT_PLAN.TASK_BANNER}
      sx={{
        p: 1.5,
        mb: 1.5,
        borderColor: isError ? 'error.main' : isDone ? 'success.main' : 'divider',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>{label}</Typography>
        <Chip
          size="small"
          color={isError ? 'error' : isDone ? 'success' : 'primary'}
          variant={isRunning ? 'outlined' : 'filled'}
          label={isError ? 'failed' : isDone ? 'completed' : (currentPhaseName ?? (loading ? 'starting…' : 'running'))}
        />
        <Box sx={{ flex: 1 }} />
        {isDone && onViewResult && (
          <Button size="small" onClick={onViewResult.onClick}>
            {onViewResult.label}
          </Button>
        )}
        <Button
          size="small"
          onClick={() => setDetailsOpen(true)}
          data-testid={CONTENT_PLAN.TASK_BANNER_DETAILS}
        >
          Details
        </Button>
        <Button
          size="small"
          color="inherit"
          onClick={onDismiss}
          data-testid={CONTENT_PLAN.TASK_BANNER_DISMISS}
        >
          Dismiss
        </Button>
      </Stack>

      {isRunning && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress
            variant={progressPercent && progressPercent > 0 ? 'determinate' : 'indeterminate'}
            value={progressPercent ?? 0}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {currentPhaseName
              ? `Phase: ${currentPhaseName}`
              : 'Waiting for the backend task to report its first phase…'}
            {' · '}task {taskId}
          </Typography>
        </Box>
      )}

      {isError && (
        <Alert
          severity="error"
          sx={{ mt: 1 }}
          data-testid={CONTENT_PLAN.TASK_BANNER_ERROR}
          action={
            <Stack direction="row" spacing={1}>
              {onRetry && (
                <Button
                  color="inherit"
                  size="small"
                  onClick={onRetry}
                  data-testid={CONTENT_PLAN.TASK_BANNER_RETRY}
                >
                  {resumeInfo ? 'Start over' : 'Retry'}
                </Button>
              )}
            </Stack>
          }
        >
          {resumeInfo?.phase && (
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Resumable from phase “{resumeInfo.phase}”.
            </Typography>
          )}
          {/* FULL error text — wrapped, never truncated (LT2 rule). */}
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {error ?? 'Task failed without an error message.'}
          </Typography>
        </Alert>
      )}

      <SDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        title={`Task details — ${label}`}
        maxWidth="md"
        fullWidth
        data-testid={CONTENT_PLAN.TASK_BANNER_DETAILS_DIALOG}
      >
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 2,
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            userSelect: 'text',
          }}
        >
          {detailsPayload}
        </Box>
      </SDialog>
    </Paper>
  );
};

export default TaskProgressBanner;
